/**
 * AgentEditPage - Unified page for creating/editing agents
 * 
 * Features:
 * - Mobile-first responsive design
 * - 4 tabs: Général, Base de connaissances, WhatsApp, Mémoire
 * - Unified component for both create and edit modes
 * - No playground (use Messenger or widget instead)
 * - URL-based navigation (/agents/new or /agents/:id/edit)
 */
import { useState, useCallback, useEffect, useId } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Save, Loader2, AlertTriangle, Upload, Brain, BookOpen, Settings, Sparkles, Users, MessageSquare, ChevronDown, Check, History, Route } from 'lucide-react'
import { listFlowTemplates } from '../lib/flowApi'
import type { FlowTemplateSummary } from '../types/flow'
import * as Select from '@radix-ui/react-select'
import { api, memoryApi, systemPromptsApi, SystemPromptListItem, MemoryScope } from '../lib/api'
import type { LlmModelOption } from '../types/llmModel'
import AgentModelPickerModal from '../components/AgentModelPickerModal'
import { HelpTooltip } from '../components/Tooltip'
import AgentTypeSelector from '../components/AgentTypeSelector'
import TemplateLibrary from '../components/TemplateLibrary'
import WhatsAppTab from '../components/WhatsAppTab'
import { AgentType, PromptTemplate } from '../data/promptTemplates'
import { useAppLanguage } from '../i18n'

// =============================================================================
// Types
// =============================================================================

type Subject = { id: number; title: string; source: string }
type Team = { id: number; title: string }

interface AgentFormData {
  name: string
  subtitle: string
  agentType: string | null
  agentMode: 'conversational' | 'guided'
  flowTemplateId: number | null
  flowTemplateVersion: number | null
  instructions: string
  messageLimit: string
  modelId: string
  topK: string
  rerankTopN: string
  disableOwnKnowledge: boolean
  subjectIds: number[]
  teamId: number | null
  systemPromptId: number | null
  memoryEnabled: boolean
  memoryEndUserEnabled: boolean
  memoryScopeIds: number[]
  autoExtractFacts: boolean
  autoExtractMinConfidence: number
}

interface AgentData {
  id: number
  name: string
  slug: string
  subtitle?: string | null
  agent_type?: string | null
  agent_mode?: string | null
  flow_template_id?: number | null
  flow_template_version?: number | null
  instructions?: string | null
  message_limit?: number | null
  model_id?: string | null
  top_k?: number | null
  rerank_top_n: number
  disable_own_knowledge: boolean
  subject_ids: number[]
  team_id?: number | null
  system_prompt_id?: number | null
  memory_enabled: boolean
  memory_end_user_enabled: boolean
  auto_extract_facts?: boolean
  auto_extract_min_confidence?: number
}

const DEFAULT_FORM: AgentFormData = {
  name: '',
  subtitle: '',
  agentType: null,
  agentMode: 'conversational',
  flowTemplateId: null,
  flowTemplateVersion: null,
  instructions: '',
  messageLimit: '',
  modelId: '',
  topK: '10',
  rerankTopN: '5',
  disableOwnKnowledge: false,
  subjectIds: [],
  teamId: null,
  systemPromptId: null,
  memoryEnabled: true,
  memoryEndUserEnabled: false,
  memoryScopeIds: [],
  autoExtractFacts: false,
  autoExtractMinConfidence: 0.75,
}

// =============================================================================
// Main Component
// =============================================================================

export default function AgentEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const formId = useId()
  const { t } = useAppLanguage()
  const isEditMode = !!id

  // State
  const [formData, setFormData] = useState<AgentFormData>(DEFAULT_FORM)
  const [iconFile, setIconFile] = useState<File | null>(null)
  const [iconPreview, setIconPreview] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [loading, setLoading] = useState(isEditMode)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<'general' | 'knowledge' | 'whatsapp' | 'memory'>('general')
  
  // Data loading states
  const [subjects, setSubjects] = useState<Subject[]>([])
  const [subjectsLoading, setSubjectsLoading] = useState(true)
  const [llmModels, setLlmModels] = useState<LlmModelOption[]>([])
  const [llmModelsLoading, setLlmModelsLoading] = useState(true)
  const [teams, setTeams] = useState<Team[]>([])
  const [teamsLoading, setTeamsLoading] = useState(true)
  const [systemPrompts, setSystemPrompts] = useState<SystemPromptListItem[]>([])
  const [isAdmin, setIsAdmin] = useState(false)
  const [availableScopes, setAvailableScopes] = useState<MemoryScope[]>([])
  const [loadingScopes, setLoadingScopes] = useState(false)
  
  // Template library
  const [selectedAgentType, setSelectedAgentType] = useState<AgentType | null>(null)
  const [showTemplateLibrary, setShowTemplateLibrary] = useState(false)
  const [modelPickerOpen, setModelPickerOpen] = useState(false)
  const [flowTemplates, setFlowTemplates] = useState<FlowTemplateSummary[]>([])
  const [flowTemplatesLoading, setFlowTemplatesLoading] = useState(false)

  // Load agent data if editing
  useEffect(() => {
    if (!isEditMode) return

    const loadAgent = async () => {
      try {
        const { data } = await api.get(`/agents/${id}`)
        setFormData({
          name: data.name,
          subtitle: data.subtitle || '',
          agentType: data.agent_type || null,
          agentMode: (data.agent_mode === 'guided' ? 'guided' : 'conversational') as 'conversational' | 'guided',
          flowTemplateId: data.flow_template_id ?? null,
          flowTemplateVersion: data.flow_template_version ?? null,
          instructions: data.instructions || '',
          messageLimit: data.message_limit?.toString() || '',
          modelId: data.model_id || '',
          topK: data.top_k?.toString() || '10',
          rerankTopN: data.rerank_top_n?.toString() || '5',
          disableOwnKnowledge: data.disable_own_knowledge || false,
          subjectIds: data.subject_ids || [],
          teamId: data.team_id ?? null,
          systemPromptId: data.system_prompt_id || null,
          memoryEnabled: data.memory_enabled ?? true,
          memoryEndUserEnabled: data.memory_end_user_enabled ?? false,
          memoryScopeIds: [],
          autoExtractFacts: data.auto_extract_facts ?? false,
          autoExtractMinConfidence: data.auto_extract_min_confidence ?? 0.75,
        })
        
        // Synchroniser selectedAgentType avec agent_type chargé
        if (data.agent_type) {
          setSelectedAgentType(data.agent_type as AgentType)
        }
        
        // Load assigned scopes
        if (data.memory_enabled) {
          const assignedScopeIds = await memoryApi.getAgentScopes(data.id)
          setFormData(prev => ({ ...prev, memoryScopeIds: assignedScopeIds }))
        }
      } catch (err: any) {
        setError(err?.response?.data?.detail || 'Failed to load agent')
      } finally {
        setLoading(false)
      }
    }

    loadAgent()
  }, [id, isEditMode])

  // Load subjects
  useEffect(() => {
    const loadSubjects = async () => {
      setSubjectsLoading(true)
      try {
        const { data } = await api.get('/subjects')
        setSubjects(Array.isArray(data) ? data : [])
      } catch (err) {
        console.error('Failed to load subjects:', err)
        setSubjects([])
      } finally {
        setSubjectsLoading(false)
      }
    }
    loadSubjects()
  }, [])

  // Load all models (Ollama + OpenAI + Anthropic)
  useEffect(() => {
    const loadModels = async () => {
      setLlmModelsLoading(true)
      try {
        const wid = localStorage.getItem('current_workspace_id')
        const params = wid ? { workspace_id: wid } : {}
        const { data } = await api.get('/models', { params })
        const modelsList = data?.models || []
        const arr = Array.isArray(modelsList) ? modelsList : []
        setLlmModels(
          arr
            .map((m: { name?: string; provider?: string; description?: string }) => ({
              name: m.name || '',
              provider: (m.provider || 'ollama').toLowerCase(),
              description: m.description,
            }))
            .filter((m: LlmModelOption) => m.name)
        )
      } catch (err) {
        console.error('Failed to load models:', err)
        setLlmModels([])
      } finally {
        setLlmModelsLoading(false)
      }
    }
    loadModels()
  }, [])

  // Load teams
  useEffect(() => {
    const loadTeams = async () => {
      setTeamsLoading(true)
      try {
        const { data } = await api.get('/agent-teams')
        // L'endpoint retourne { items: [...], total: number }
        const teamsList = data?.items || []
        setTeams(Array.isArray(teamsList) ? teamsList : [])
      } catch (err) {
        console.error('Failed to load teams:', err)
        setTeams([])
      } finally {
        setTeamsLoading(false)
      }
    }
    loadTeams()
  }, [])

  // Load system prompts (admin only)
  useEffect(() => {
    const loadSystemPrompts = async () => {
      try {
        const user = JSON.parse(localStorage.getItem('user') || 'null')
        setIsAdmin(user?.is_superuser === true)
        
        if (user?.is_superuser) {
          const response = await systemPromptsApi.list()
          setSystemPrompts(response.items || [])
        }
      } catch (err) {
        console.error('Failed to load system prompts:', err)
      }
    }
    loadSystemPrompts()
  }, [])

  // Load flow templates (parcours guidé)
  useEffect(() => {
    const loadFlowTemplates = async () => {
      setFlowTemplatesLoading(true)
      try {
        const items = await listFlowTemplates()
        setFlowTemplates(items)
      } catch (err) {
        console.error('Failed to load flow templates:', err)
        setFlowTemplates([])
      } finally {
        setFlowTemplatesLoading(false)
      }
    }
    loadFlowTemplates()
  }, [])

  // Load memory scopes
  useEffect(() => {
    const loadScopes = async () => {
      setLoadingScopes(true)
      try {
        const scopesRes = await memoryApi.listScopes()
        const items = scopesRes?.items || []
        setAvailableScopes(items.filter(s => s.scope_type === 'tenant'))
      } catch (err) {
        console.error('Failed to load scopes:', err)
        setAvailableScopes([])
      } finally {
        setLoadingScopes(false)
      }
    }
    loadScopes()
  }, [])

  // Handle form changes
  const handleChange = useCallback((field: keyof AgentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    if (error) setError(null)
  }, [error])

  // Handle subject toggle
  const handleSubjectToggle = useCallback((subjectId: number) => {
    setFormData(prev => ({
      ...prev,
      subjectIds: prev.subjectIds.includes(subjectId)
        ? prev.subjectIds.filter(id => id !== subjectId)
        : [...prev.subjectIds, subjectId]
    }))
  }, [])

  // Handle scope toggle
  const handleScopeToggle = useCallback((scopeId: number) => {
    setFormData(prev => ({
      ...prev,
      memoryScopeIds: prev.memoryScopeIds.includes(scopeId)
        ? prev.memoryScopeIds.filter(id => id !== scopeId)
        : [...prev.memoryScopeIds, scopeId]
    }))
  }, [])

  // Handle icon file change
  const handleIconChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setIconFile(file)
      const reader = new FileReader()
      reader.onloadend = () => setIconPreview(reader.result as string)
      reader.readAsDataURL(file)
    }
  }, [])

  // Handle agent type selection
  const handleAgentTypeChange = useCallback((type: AgentType | null) => {
    setSelectedAgentType(type)
    setFormData(prev => ({ ...prev, agentType: type }))
    setShowTemplateLibrary(!!type)
  }, [])

  // Handle template selection
  const handleTemplateSelect = useCallback((template: PromptTemplate) => {
    handleChange('instructions', template.content)
    setShowTemplateLibrary(false)
  }, [handleChange])

  // Handle submit
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!formData.name.trim()) {
      setError('Le nom de l\'agent est requis')
      return
    }
    if (formData.agentMode === 'guided' && !formData.flowTemplateId) {
      setError('Sélectionnez un parcours pour un agent guidé')
      return
    }

    setSaving(true)
    setError(null)

    try {
      const payload: Record<string, any> = {
        name: formData.name.trim(),
        subtitle: formData.subtitle.trim() || null,
        agent_type: formData.agentType || null,
        agent_mode: formData.agentMode,
        flow_template_id: formData.agentMode === 'guided' ? formData.flowTemplateId : null,
        flow_template_version: formData.agentMode === 'guided' ? formData.flowTemplateVersion : null,
        instructions: formData.instructions.trim() || null,
        message_limit: formData.messageLimit ? parseInt(formData.messageLimit) : null,
        model_id: formData.modelId || null,
        top_k: formData.topK ? parseInt(formData.topK) : 10,
        rerank_top_n: formData.rerankTopN ? parseInt(formData.rerankTopN) : 5,
        disable_own_knowledge: formData.disableOwnKnowledge,
        subject_ids: formData.subjectIds,
        memory_enabled: formData.memoryEnabled,
        memory_end_user_enabled: formData.memoryEndUserEnabled,
        auto_extract_facts: formData.autoExtractFacts,
        auto_extract_min_confidence: formData.autoExtractMinConfidence,
      }

      // Team assignment
      if (formData.teamId !== null) {
        payload.team_id = formData.teamId
      } else if (isEditMode) {
        payload.team_id = 0 // Clear team
      }

      // Admin-only: system prompt override
      if (isAdmin && formData.systemPromptId !== null) {
        payload.system_prompt_id = formData.systemPromptId
      }

      let savedAgent: AgentData

      if (isEditMode) {
        const { data } = await api.patch(`/agents/${id}`, payload)
        savedAgent = data
      } else {
        const { data } = await api.post('/agents', payload)
        savedAgent = data
      }

      // Upload icon if selected
      if (iconFile && savedAgent.id) {
        const iconFormData = new FormData()
        iconFormData.append('file', iconFile)
        try {
          await api.post(`/agents/${savedAgent.id}/icon`, iconFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          })
        } catch (iconErr) {
          console.warn('Failed to upload icon:', iconErr)
        }
      }

      // Save memory scope assignments
      if (savedAgent.id && formData.memoryEnabled) {
        try {
          await memoryApi.updateAgentScopes(savedAgent.id, formData.memoryScopeIds)
        } catch (scopeErr) {
          console.warn('Failed to update memory scopes:', scopeErr)
        }
      }

      // Navigate back to agents list
      navigate('/agents')
    } catch (err: any) {
      const rawDetail = err?.response?.data?.detail
      const detail = typeof rawDetail === 'object' && rawDetail !== null
        ? rawDetail.message || rawDetail.error || JSON.stringify(rawDetail)
        : rawDetail || err?.message || 'Erreur lors de l\'enregistrement'
      setError(detail)
    } finally {
      setSaving(false)
    }
  }, [formData, isEditMode, id, iconFile, isAdmin, navigate])

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full bg-zinc-950">
        <Loader2 className="w-8 h-8 text-emerald-500 animate-spin" />
      </div>
    )
  }

  return (
    <div className="relative overflow-hidden">
      {/* Background gradients - same as AgentsPage */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />
      <div aria-hidden className="absolute inset-0 -z-20 bg-[radial-gradient(circle_at_15%_18%,rgba(236,72,153,0.14),transparent_60%),radial-gradient(circle_at_85%_18%,rgba(168,85,247,0.17),transparent_55%)] dark:bg-[radial-gradient(circle_at_12%_18%,rgba(236,72,153,0.12),transparent_55%),radial-gradient(circle_at_88%_20%,rgba(168,85,247,0.15),transparent_50%)]" />

      {/* Content wrapper - same as AgentsPage */}
      <div className="relative mx-auto flex max-w-[1600px] flex-col gap-4 px-1 py-3 sm:px-6 sm:py-6">
        {/* Header - Sticky at top */}
        <div className="sticky top-0 z-30 bg-zinc-900 border-b border-zinc-800 rounded-xl">
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-2 lg:gap-3 min-w-0 flex-1">
            <button
              onClick={() => navigate('/agents')}
              className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 transition-colors flex-shrink-0"
              aria-label="Retour"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            {isEditMode && id && (
              <button
                type="button"
                onClick={() => navigate(`/conversations?agent_id=${id}`)}
                className="p-2 rounded-lg text-zinc-400 hover:text-emerald-400 hover:bg-zinc-800 transition-colors flex-shrink-0"
                title={t.agents.agentCard.history}
                aria-label={t.agents.agentCard.history}
              >
                <History className="w-5 h-5" />
              </button>
            )}
            <div className="flex items-center gap-2 lg:gap-3 min-w-0">
              <div className="p-1.5 lg:p-2 rounded-lg bg-emerald-900/30 flex-shrink-0">
                <Sparkles className="w-4 h-4 lg:w-5 lg:h-5 text-emerald-400" />
              </div>
              <h1 className="text-base lg:text-lg font-semibold text-white truncate">
                {isEditMode ? formData.name || 'Modifier l\'agent' : 'Nouvel agent'}
              </h1>
            </div>
          </div>
          <button
            type="submit"
            form={formId}
            className="inline-flex items-center gap-2 px-3 lg:px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white text-sm font-medium transition-colors disabled:opacity-50 flex-shrink-0"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="hidden sm:inline">Enregistrement...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                <span className="hidden sm:inline">Enregistrer</span>
              </>
            )}
          </button>
        </div>

        {/* Error banner */}
        {error && (
          <div className="mx-4 mb-3 p-3 rounded-lg bg-red-900/20 border border-red-800 text-red-300 flex items-center gap-2 text-sm">
            <AlertTriangle className="w-5 h-5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        )}

        {/* Tabs - Horizontal scroll on mobile */}
        <div className="overflow-x-auto scrollbar-none">
          <div className="flex gap-1 px-4 min-w-max">
            <button
              type="button"
              onClick={() => setActiveTab('general')}
              className={`px-3 lg:px-4 py-2 lg:py-3 text-xs lg:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'general'
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-zinc-400 hover:text-zinc-300'
              }`}
            >
              Général
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('knowledge')}
              className={`px-3 lg:px-4 py-2 lg:py-3 text-xs lg:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'knowledge'
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-zinc-400 hover:text-zinc-300'
              }`}
            >
              Base de connaissances
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('whatsapp')}
              className={`px-3 lg:px-4 py-2 lg:py-3 text-xs lg:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'whatsapp'
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-zinc-400 hover:text-zinc-300'
              }`}
            >
              WhatsApp
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('memory')}
              className={`px-3 lg:px-4 py-2 lg:py-3 text-xs lg:text-sm font-medium transition-colors border-b-2 whitespace-nowrap ${
                activeTab === 'memory'
                  ? 'border-emerald-500 text-emerald-400'
                  : 'border-transparent text-zinc-400 hover:text-zinc-300'
              }`}
            >
              🧠 Mémoire
            </button>
          </div>
        </div>
      </div>

        {/* Body - Form */}
        <form 
          id={formId}
          onSubmit={handleSubmit}
          className="flex flex-col gap-4"
        >
        {/* Tab: Général */}
        {activeTab === 'general' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6">
              {/* Column 1: Basic Info */}
              <div className="space-y-4 lg:space-y-6">
                <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-5">
                  <h3 className="text-xs lg:text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3 lg:mb-4 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Informations générales
                  </h3>
                  
                  <div className="space-y-3 lg:space-y-4">
                    {/* Name */}
                    <div>
                      <label htmlFor={`${formId}-name`} className="block text-sm font-medium text-zinc-300 mb-1">
                        Nom <span className="text-red-400">*</span>
                      </label>
                      <input
                        id={`${formId}-name`}
                        type="text"
                        value={formData.name}
                        onChange={e => handleChange('name', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-sm lg:text-base"
                        placeholder="Ex: Assistant commercial"
                        required
                      />
                    </div>

                    {/* Subtitle */}
                    <div>
                      <label htmlFor={`${formId}-subtitle`} className="block text-sm font-medium text-zinc-300 mb-1">
                        Sous-titre
                      </label>
                      <input
                        id={`${formId}-subtitle`}
                        type="text"
                        value={formData.subtitle}
                        onChange={e => handleChange('subtitle', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-sm lg:text-base"
                        placeholder="Tagline visible dans le chat"
                      />
                    </div>

                    {/* Icon */}
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2">
                        Icône
                      </label>
                      <div className="flex items-center gap-4">
                        {iconPreview ? (
                          <img src={iconPreview} alt="Preview" className="w-12 h-12 lg:w-16 lg:h-16 rounded-xl object-cover border border-zinc-700" />
                        ) : (
                          <div className="w-12 h-12 lg:w-16 lg:h-16 rounded-xl bg-zinc-800 border border-zinc-700 flex items-center justify-center">
                            <Upload className="w-5 h-5 lg:w-6 lg:h-6 text-zinc-500" />
                          </div>
                        )}
                        <label className="flex-1">
                          <input
                            type="file"
                            accept="image/png,image/jpeg,image/webp,image/svg+xml"
                            onChange={handleIconChange}
                            className="hidden"
                          />
                          <span className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors cursor-pointer text-xs lg:text-sm">
                            <Upload className="w-4 h-4" />
                            Choisir une image
                          </span>
                        </label>
                      </div>
                    </div>

                    {/* Agent mode: conversationnel vs guidé */}
                    <div>
                      <label className="block text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
                        <Route className="w-4 h-4 text-emerald-400" />
                        Mode de l&apos;agent
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <button
                          type="button"
                          onClick={() => handleChange('agentMode', 'conversational')}
                          className={`rounded-xl border px-3 py-2.5 text-left text-sm transition ${
                            formData.agentMode === 'conversational'
                              ? 'border-emerald-500/60 bg-emerald-950/40 text-emerald-100'
                              : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600'
                          }`}
                        >
                          <span className="font-medium">Conversationnel</span>
                          <p className="mt-0.5 text-xs opacity-80">Chat libre avec RAG et outils</p>
                        </button>
                        <button
                          type="button"
                          onClick={() => handleChange('agentMode', 'guided')}
                          className={`rounded-xl border px-3 py-2.5 text-left text-sm transition ${
                            formData.agentMode === 'guided'
                              ? 'border-emerald-500/60 bg-emerald-950/40 text-emerald-100'
                              : 'border-zinc-700 bg-zinc-800/50 text-zinc-400 hover:border-zinc-600'
                          }`}
                        >
                          <span className="font-medium">Agent guidé</span>
                          <p className="mt-0.5 text-xs opacity-80">Parcours structuré + livrables</p>
                        </button>
                      </div>
                    </div>

                    {formData.agentMode === 'guided' && (
                      <div>
                        <label htmlFor={`${formId}-flow-template`} className="block text-sm font-medium text-zinc-300 mb-1">
                          Parcours <span className="text-red-400">*</span>
                        </label>
                        {flowTemplatesLoading ? (
                          <div className="flex items-center gap-2 text-sm text-zinc-500">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Chargement des parcours…
                          </div>
                        ) : (
                          <select
                            id={`${formId}-flow-template`}
                            value={formData.flowTemplateId ?? ''}
                            onChange={e => {
                              const id = e.target.value ? Number(e.target.value) : null
                              const tpl = flowTemplates.find(t => t.id === id)
                              setFormData(prev => ({
                                ...prev,
                                flowTemplateId: id,
                                flowTemplateVersion: tpl?.version ?? null,
                              }))
                            }}
                            className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-sm"
                          >
                            <option value="">— Choisir un parcours —</option>
                            {flowTemplates.filter(t => t.scope === 'platform').length > 0 && (
                              <optgroup label="Catalogue plateforme">
                                {flowTemplates.filter(t => t.scope === 'platform').map(t => (
                                  <option key={t.id} value={t.id}>
                                    {t.name} (v{t.version})
                                  </option>
                                ))}
                              </optgroup>
                            )}
                            {flowTemplates.filter(t => t.scope === 'workspace').length > 0 && (
                              <optgroup label="Mes parcours">
                                {flowTemplates.filter(t => t.scope === 'workspace').map(t => (
                                  <option key={t.id} value={t.id}>
                                    {t.name} (v{t.version})
                                  </option>
                                ))}
                              </optgroup>
                            )}
                          </select>
                        )}
                        {formData.flowTemplateId && (
                          <p className="mt-1 text-xs text-zinc-500">
                            {flowTemplates.find(t => t.id === formData.flowTemplateId)?.description || 'Associez une base de connaissances pour enrichir les livrables.'}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Agent Type Selector */}
                    <AgentTypeSelector
                      value={selectedAgentType}
                      onChange={handleAgentTypeChange}
                    />

                    {/* Template Library */}
                    {showTemplateLibrary && selectedAgentType && (
                      <div className="p-3 lg:p-4 bg-zinc-900 border border-zinc-800 rounded-xl">
                        <TemplateLibrary
                          agentType={selectedAgentType}
                          currentPrompt={formData.instructions}
                          onSelectTemplate={handleTemplateSelect}
                        />
                      </div>
                    )}

                    {/* Instructions */}
                    <div>
                      <label htmlFor={`${formId}-instructions`} className="block text-sm font-medium text-zinc-300 mb-1">
                        💬 Personnalité
                      </label>
                      <textarea
                        id={`${formId}-instructions`}
                        value={formData.instructions}
                        onChange={e => handleChange('instructions', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 min-h-[100px] lg:min-h-[120px] text-sm lg:text-base"
                        placeholder="Décrivez le comportement de votre agent..."
                      />
                      {formData.instructions && (
                        <button
                          type="button"
                          onClick={() => setShowTemplateLibrary(!showTemplateLibrary)}
                          className="mt-2 text-xs text-emerald-400 hover:text-emerald-300 transition-colors"
                        >
                          {showTemplateLibrary ? '✕ Masquer les templates' : '📚 Changer de template'}
                        </button>
                      )}
                    </div>

                    {/* Message Limit */}
                    <div>
                      <label htmlFor={`${formId}-msglimit`} className="block text-sm font-medium text-zinc-300 mb-1">
                        Limite de messages
                      </label>
                      <input
                        id={`${formId}-msglimit`}
                        type="number"
                        min={1}
                        value={formData.messageLimit}
                        onChange={e => handleChange('messageLimit', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-sm lg:text-base"
                        placeholder="Aucune limite"
                      />
                      <p className="mt-1 text-xs text-zinc-500">Nombre maximum de messages par session</p>
                    </div>
                  </div>
                </section>
              </div>

              {/* Column 2: AI Settings */}
              <div className="space-y-4 lg:space-y-6">
                <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-5">
                  <h3 className="text-xs lg:text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3 lg:mb-4 flex items-center gap-2">
                    <Brain className="w-4 h-4" />
                    Intelligence artificielle
                  </h3>
                  
                  <div className="space-y-3 lg:space-y-4">
                    {/* Model */}
                    <div>
                      <label htmlFor={`${formId}-model`} className="block text-sm font-medium text-zinc-300 mb-1 flex items-center gap-1">
                        🧠 Modèle
                        <HelpTooltip content="Le modèle de langage utilisé pour générer les réponses" position="right" />
                      </label>
                      <div className="flex gap-2">
                        <input
                          id={`${formId}-model`}
                          readOnly
                          className="min-w-0 flex-1 px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 text-sm lg:text-base cursor-default"
                          value={
                            formData.modelId
                              ? llmModels.find(m => m.name === formData.modelId)?.name || formData.modelId
                              : t.agents.agentEditor.modelDefault
                          }
                        />
                        <button
                          type="button"
                          onClick={() => setModelPickerOpen(true)}
                          disabled={llmModelsLoading}
                          className="shrink-0 rounded-full border border-emerald-700/60 bg-emerald-950/40 px-4 py-2 text-xs font-semibold text-emerald-200 transition hover:bg-emerald-900/50 disabled:opacity-50"
                        >
                          {t.agents.modelPicker.browse}
                        </button>
                      </div>
                      <AgentModelPickerModal
                        open={modelPickerOpen}
                        onClose={() => setModelPickerOpen(false)}
                        models={llmModels}
                        value={formData.modelId}
                        onApply={name => handleChange('modelId', name)}
                        loading={llmModelsLoading}
                        defaultOptionLabel={t.agents.agentEditor.modelDefault}
                      />
                    </div>

                    {/* Top K */}
                    <div>
                      <label htmlFor={`${formId}-topk`} className="block text-sm font-medium text-zinc-300 mb-1 flex items-center gap-1">
                        Top K
                        <HelpTooltip content="Nombre de chunks récupérés pour le contexte" position="right" />
                      </label>
                      <input
                        id={`${formId}-topk`}
                        type="number"
                        min={1}
                        max={50}
                        value={formData.topK}
                        onChange={e => handleChange('topK', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-sm lg:text-base"
                        placeholder="10"
                      />
                      <p className="mt-1 text-xs text-zinc-500">Nombre de contextes RAG (défaut: 10)</p>
                    </div>

                    {/* Rerank Top N */}
                    <div>
                      <label htmlFor={`${formId}-rerank`} className="block text-sm font-medium text-zinc-300 mb-1 flex items-center gap-1">
                        🎯 Rerank Top N
                        <HelpTooltip content="Résultats finaux après reranking sémantique" position="right" />
                      </label>
                      <input
                        id={`${formId}-rerank`}
                        type="number"
                        min={1}
                        max={20}
                        value={formData.rerankTopN}
                        onChange={e => handleChange('rerankTopN', e.target.value)}
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-sm lg:text-base"
                        placeholder="5"
                      />
                      <p className="mt-1 text-xs text-zinc-500">Résultats après reranking (1-20, défaut: 5)</p>
                    </div>

                    {/* Strict mode */}
                    <div className="pt-2">
                      <label className="flex items-start gap-3 cursor-pointer p-3 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors">
                        <input
                          type="checkbox"
                          checked={formData.disableOwnKnowledge}
                          onChange={e => handleChange('disableOwnKnowledge', e.target.checked)}
                          className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                        />
                        <div>
                          <span className="text-sm font-medium text-zinc-200">
                            Réponses strictes
                          </span>
                          <p className="text-xs text-zinc-500 mt-1">
                            L'agent ne répondra qu'avec les informations de votre base de connaissances
                          </p>
                        </div>
                      </label>
                    </div>
                  </div>
                </section>

                {/* Team Assignment */}
                <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-5">
                  <h3 className="text-xs lg:text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3 lg:mb-4 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Équipe
                  </h3>
                  
                  <div>
                    <label htmlFor={`${formId}-team`} className="block text-sm font-medium text-zinc-300 mb-1">
                      👥 Équipe
                    </label>
                    <Select.Root
                      value={formData.teamId?.toString() || "none"}
                      onValueChange={(value) => handleChange('teamId', value === "none" ? null : parseInt(value))}
                      disabled={teamsLoading}
                    >
                      <Select.Trigger
                        className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 text-sm lg:text-base flex items-center justify-between disabled:opacity-50"
                      >
                        <Select.Value placeholder="Sans équipe" />
                        <Select.Icon>
                          <ChevronDown className="w-4 h-4 text-zinc-400" />
                        </Select.Icon>
                      </Select.Trigger>
                      <Select.Portal>
                        <Select.Content
                          className="overflow-hidden bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50"
                          position="popper"
                          sideOffset={5}
                        >
                          <Select.Viewport className="p-1">
                            <Select.Item
                              value="none"
                              className="relative flex items-center px-8 py-2 text-sm text-zinc-100 rounded-md outline-none cursor-pointer hover:bg-zinc-700 focus:bg-zinc-700 data-[state=checked]:bg-emerald-900/30"
                            >
                              <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
                                <Check className="w-4 h-4 text-emerald-400" />
                              </Select.ItemIndicator>
                              <Select.ItemText>Sans équipe</Select.ItemText>
                            </Select.Item>
                            {teams.map(team => (
                              <Select.Item
                                key={team.id}
                                value={team.id.toString()}
                                className="relative flex items-center px-8 py-2 text-sm text-zinc-100 rounded-md outline-none cursor-pointer hover:bg-zinc-700 focus:bg-zinc-700 data-[state=checked]:bg-emerald-900/30"
                              >
                                <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
                                  <Check className="w-4 h-4 text-emerald-400" />
                                </Select.ItemIndicator>
                                <Select.ItemText>{team.title}</Select.ItemText>
                              </Select.Item>
                            ))}
                          </Select.Viewport>
                        </Select.Content>
                      </Select.Portal>
                    </Select.Root>
                  </div>
                </section>

                {/* Admin: System Prompt Override */}
                {isAdmin && (
                  <section className="bg-zinc-900 border border-amber-800/50 rounded-xl p-4 lg:p-5">
                    <h3 className="text-xs lg:text-sm font-medium text-amber-400 uppercase tracking-wider mb-3 lg:mb-4">
                      🔒 Admin - Prompt système
                    </h3>
                    
                    <div>
                      <label htmlFor={`${formId}-sysprompt`} className="block text-sm font-medium text-zinc-300 mb-1">
                        Prompt système
                      </label>
                      <Select.Root
                        value={formData.systemPromptId?.toString() || "default"}
                        onValueChange={(value) => handleChange('systemPromptId', value === "default" ? null : parseInt(value))}
                      >
                        <Select.Trigger
                          className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-amber-500/50 text-sm lg:text-base flex items-center justify-between"
                        >
                          <Select.Value placeholder="— Prompt par défaut —" />
                          <Select.Icon>
                            <ChevronDown className="w-4 h-4 text-zinc-400" />
                          </Select.Icon>
                        </Select.Trigger>
                        <Select.Portal>
                          <Select.Content
                            className="overflow-hidden bg-zinc-800 border border-zinc-700 rounded-lg shadow-xl z-50"
                            position="popper"
                            sideOffset={5}
                          >
                            <Select.Viewport className="p-1">
                              <Select.Item
                                value="default"
                                className="relative flex items-center px-8 py-2 text-sm text-zinc-100 rounded-md outline-none cursor-pointer hover:bg-zinc-700 focus:bg-zinc-700 data-[state=checked]:bg-emerald-900/30"
                              >
                                <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
                                  <Check className="w-4 h-4 text-emerald-400" />
                                </Select.ItemIndicator>
                                <Select.ItemText>— Prompt par défaut —</Select.ItemText>
                              </Select.Item>
                              {systemPrompts.map(sp => (
                                <Select.Item
                                  key={sp.id}
                                  value={sp.id.toString()}
                                  className="relative flex items-center px-8 py-2 text-sm text-zinc-100 rounded-md outline-none cursor-pointer hover:bg-zinc-700 focus:bg-zinc-700 data-[state=checked]:bg-emerald-900/30"
                                >
                                  <Select.ItemIndicator className="absolute left-2 inline-flex items-center">
                                    <Check className="w-4 h-4 text-emerald-400" />
                                  </Select.ItemIndicator>
                                  <Select.ItemText>{sp.name}</Select.ItemText>
                                </Select.Item>
                              ))}
                            </Select.Viewport>
                          </Select.Content>
                        </Select.Portal>
                      </Select.Root>
                      <p className="mt-1 text-xs text-amber-400/70">
                        Remplace le prompt système par défaut pour cet agent
                      </p>
                    </div>
                  </section>
                )}

                {/* Test link */}
                {isEditMode && (
                  <div className="p-4 bg-blue-900/20 border border-blue-800/30 rounded-xl">
                    <div className="flex items-center gap-2 text-blue-400 mb-2">
                      <MessageSquare className="w-4 h-4" />
                      <span className="text-sm font-medium">Tester l'agent</span>
                    </div>
                    <p className="text-xs text-blue-300/70 mb-3">
                      Testez votre agent dans le Messenger ou via le widget
                    </p>
                    <button
                      type="button"
                      onClick={() => navigate(`/messenger/${id}`)}
                      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-xs font-medium transition-colors"
                    >
                      <MessageSquare className="w-3.5 h-3.5" />
                      Ouvrir dans Messenger
                    </button>
                  </div>
                )}
              </div>
            </div>
        )}

        {/* Tab: Base de connaissances */}
        {activeTab === 'knowledge' && (
          <div>
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-5">
              <h3 className="text-xs lg:text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3 lg:mb-4 flex items-center gap-2">
                <BookOpen className="w-4 h-4" />
                Base de connaissances
              </h3>
              
              <p className="text-xs text-zinc-500 mb-4">
                Sélectionnez les documents que votre agent peut consulter pour répondre aux questions
              </p>

              <div className="space-y-2">
                {subjectsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="w-5 h-5 text-zinc-500 animate-spin" />
                  </div>
                ) : subjects.length === 0 ? (
                  <div className="text-center py-8 text-zinc-500 text-sm">
                    <BookOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>Aucune base de connaissances disponible</p>
                    <p className="text-xs mt-1">Créez-en une depuis la section Connaissances</p>
                  </div>
                ) : (
                  subjects.map(subject => {
                    const checked = formData.subjectIds.includes(subject.id)
                    return (
                      <label
                        key={subject.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          checked 
                            ? 'bg-emerald-900/20 border-emerald-700' 
                            : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => handleSubjectToggle(subject.id)}
                          className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium text-zinc-200 truncate">{subject.title}</div>
                          <div className="text-xs text-zinc-500 truncate">{subject.source}</div>
                        </div>
                      </label>
                    )
                  })
                )}
              </div>

              {/* Selected count */}
              <div className="mt-4 pt-4 border-t border-zinc-800">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-zinc-400">Sélectionnés</span>
                  <span className="font-medium text-emerald-400">
                    {formData.subjectIds.length} / {subjects.length}
                  </span>
                </div>
              </div>
            </section>
          </div>
        )}

        {/* Tab: WhatsApp */}
        {activeTab === 'whatsapp' && (
          <div>
            {isEditMode && id ? (
              <WhatsAppTab agentId={parseInt(id)} />
            ) : (
              <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-8 text-center">
                <p className="text-zinc-400">
                  La configuration WhatsApp sera disponible après la création de l'agent
                </p>
              </div>
            )}
          </div>
        )}

        {/* Tab: Mémoire */}
        {activeTab === 'memory' && (
          <div>
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 lg:p-5">
              <h3 className="text-xs lg:text-sm font-medium text-zinc-400 uppercase tracking-wider mb-3 lg:mb-4">
                🧠 Système de mémoire
              </h3>
              
              <div className="space-y-4">
                <label className="flex items-start gap-3 cursor-pointer p-3 lg:p-4 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.memoryEnabled}
                    onChange={e => handleChange('memoryEnabled', e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-zinc-200">
                      💼 Mémoire Business (Tenant)
                    </span>
                    <p className="text-xs text-zinc-500 mt-1">
                      Active la mémoire partagée pour vos connaissances métier
                    </p>
                  </div>
                </label>

                <label className="flex items-start gap-3 cursor-pointer p-3 lg:p-4 rounded-lg border border-zinc-700 hover:border-zinc-600 transition-colors">
                  <input
                    type="checkbox"
                    checked={formData.memoryEndUserEnabled}
                    onChange={e => handleChange('memoryEndUserEnabled', e.target.checked)}
                    className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                  />
                  <div>
                    <span className="text-sm font-medium text-zinc-200">
                      👤 Mémoire End-User (Clients)
                    </span>
                    <p className="text-xs text-zinc-500 mt-1">
                      Mémorise des informations spécifiques par client identifié
                    </p>
                  </div>
                </label>

                {formData.memoryEnabled && (
                  <div className="mt-6 p-4 lg:p-5 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-sm font-medium text-zinc-300">
                        📚 Scopes Assignés
                      </h4>
                      {loadingScopes && (
                        <Loader2 className="w-4 h-4 animate-spin text-zinc-500" />
                      )}
                    </div>
                    
                    {availableScopes.length === 0 && !loadingScopes ? (
                      <p className="text-sm text-zinc-500">
                        Aucun scope disponible. <a href="/memory" className="underline hover:text-zinc-400">Créez-en un</a>.
                      </p>
                    ) : (
                      <div className="space-y-2">
                        {availableScopes.map(scope => (
                          <label
                            key={scope.id}
                            className="flex items-start gap-3 p-3 rounded-lg hover:bg-zinc-700/30 cursor-pointer transition-colors border border-zinc-700/50"
                          >
                            <input
                              type="checkbox"
                              checked={formData.memoryScopeIds.includes(scope.id)}
                              onChange={() => handleScopeToggle(scope.id)}
                              className="mt-0.5 w-4 h-4 rounded border-zinc-600 bg-zinc-700 text-emerald-500"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-medium text-zinc-200">
                                {scope.name}
                              </div>
                              {scope.description && (
                                <div className="text-xs text-zinc-500 mt-1">
                                  {scope.description}
                                </div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                    
                    <div className="mt-4 p-3 bg-blue-900/20 border border-blue-800/30 rounded-lg">
                      <p className="text-xs text-blue-400">
                        {formData.memoryScopeIds.length === 0 
                          ? '💡 Aucun scope sélectionné - tous les scopes business seront utilisés'
                          : `✓ ${formData.memoryScopeIds.length} scope(s) sélectionné(s)`
                        }
                      </p>
                    </div>
                  </div>
                )}

                {/* Section Extraction Automatique */}
                <div className="mt-4 p-4 lg:p-5 rounded-xl border border-purple-100/70 bg-gradient-to-r from-purple-50/5 to-indigo-50/5 dark:border-purple-900/60">
                  <h5 className="text-sm font-medium text-zinc-200 mb-3">
                    🤖 Extraction Automatique
                  </h5>
                  
                  <label className="flex items-start gap-3 cursor-pointer mb-3">
                    <input
                      type="checkbox"
                      checked={formData.autoExtractFacts}
                      onChange={e => handleChange('autoExtractFacts', e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-zinc-600 text-purple-500"
                    />
                    <div>
                      <span className="text-sm text-zinc-200">
                        Activer l'apprentissage automatique
                      </span>
                      <p className="text-xs text-zinc-500 mt-0.5">
                        L'agent apprendra automatiquement des conversations (nom, préférences, etc.)
                      </p>
                    </div>
                  </label>
                  
                  {formData.autoExtractFacts && (
                    <div className="mt-3 pl-7">
                      <label className="text-xs text-zinc-400 mb-1 block">
                        Seuil de confiance minimum: {Math.round(formData.autoExtractMinConfidence * 100)}%
                      </label>
                      <input
                        type="range"
                        min="0.5"
                        max="1"
                        step="0.05"
                        value={formData.autoExtractMinConfidence}
                        onChange={e => handleChange('autoExtractMinConfidence', parseFloat(e.target.value))}
                        className="w-full h-2 bg-zinc-700 rounded-lg appearance-none cursor-pointer"
                      />
                      <p className="text-xs text-zinc-500 mt-1">
                        Plus le seuil est élevé, moins de faits seront extraits mais plus ils seront fiables
                      </p>
                    </div>
                  )}
                </div>

                <div className="p-3 lg:p-4 rounded-xl bg-blue-900/20 border border-blue-800/30">
                  <p className="text-xs text-blue-400">
                    💡 Gérez vos scopes et faits mémoire dans la page <a href="/memory" className="underline hover:text-blue-300">Mémoire</a>
                  </p>
                </div>
              </div>
            </section>
          </div>
        )}
        </form>
      </div>
    </div>
  )
}
